<template>
  <div class="hs-table-wrap hs-responsive-table relative">
    <table class="hs-table w-full text-sm text-left rtl:text-right">
      <thead>
        <tr>
          <!-- Dynamic column headers -->
          <th v-for="(header, index) in headers" :key="index" scope="col" :class="[
            'px-4 py-3 font-semibold tracking-wide',
            index === 0
              ? 'sticky left-0 hs-table-head-sticky'
              : '',
            isActionColumn(header) ? 'hs-table-action-head' : '',
            // index === headers.length - 1
            //   ? 'sticky right-0 z-10 bg-gray-300 dark:bg-slate-600 sm:bg-gray-300 dark:sm:bg-slate-600'
            //   : '',
          ]">
            <slot :name="'header-' + getSlotName(header)" :header="header">
              {{ header }}
            </slot>
          </th>
        </tr>
      </thead>
      <tbody v-if="data.length" class="divide-y divide-gray-300 dark:divide-slate-700">
        <tr v-for="(row, rowIndex) in data" :key="rowIndex" class="hs-table-row border-b"
          :class="rowClickable ? 'cursor-pointer' : ''" @click="onRowClick(row)">
          <td v-for="(header, index) in headers" :key="index" :class="[
            'px-4 py-3',
            index === 0
              ? 'sticky left-0 hs-table-cell-sticky'
              : '',
            isActionColumn(header) ? 'hs-table-action-cell' : '',
            // index === headers.length - 1
            //   ? 'sticky right-0 z-10 bg-inherit sm:bg-gray-300 dark:sm:bg-slate-600'
            //   : '',
          ]">
            <slot :name="'cell-' + getSlotName(header)" :row="row" :header="header"
              :value="row[headerKeyMapping[header]]">
              {{
                row[headerKeyMapping[header]]
                  ? row[headerKeyMapping[header]]
                  : "N/A"
              }}
            </slot>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td :colspan="headers.length" class="hs-table-empty-cell">
            <slot name="empty">
              <div class="hs-table-empty">
                <div class="hs-table-empty__icon">
                  <vue-feather type="inbox" size="22" />
                </div>
                <div>
                  <p>No records found</p>
                  <span>Try adjusting filters or refreshing the page.</span>
                </div>
              </div>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "HSTable",
  props: {
    headers: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    headerKeyMapping: {
      type: Object,
      required: true,
    },
    rowClickable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["row-click"],
  methods: {
    getSlotName(header) {
      // Convert header text to a valid slot name
      return header.toLowerCase().replace(/\s+/g, "_");
    },
    isActionColumn(header) {
      const normalizedHeader = String(header || "").toLowerCase();
      const mappedKey = String(this.headerKeyMapping?.[header] || "").toLowerCase();
      return normalizedHeader === "action" ||
        normalizedHeader === "actions" ||
        mappedKey === "action" ||
        mappedKey === "actions";
    },
    onRowClick(row) {
      if (this.rowClickable) {
        this.$emit("row-click", row);
      }
    },
  },
};
</script>

<style scoped>
.hs-table-wrap {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  box-shadow: none;
  isolation: isolate;
  max-width: 100%;
  min-width: 0;
  position: relative;
  width: 100%;
  z-index: 0;
}

table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: max(760px, 100%);
}

thead {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 72%, var(--hs-surface, #ffffff));
  color: var(--hs-muted, #64748b);
}

thead th {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  position: relative;
  white-space: nowrap;
}

.hs-table-row {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  transition: background-color 0.2s ease;
}

.hs-table-row td {
  border-bottom: 1px solid color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 78%, transparent);
  color: var(--hs-text, #0f172a);
  vertical-align: middle;
}

.hs-table-action-head,
.hs-table-action-cell {
  text-align: center;
  width: 1%;
}

.hs-table-action-cell {
  white-space: nowrap;
}

.hs-table-action-cell :deep(.hs-action-menu),
.hs-table-action-cell :deep(.ui-button),
.hs-table-action-cell :deep(.hs-button) {
  margin-left: auto;
}

.hs-table-row:last-child td {
  border-bottom: 0;
}

.hs-table-row:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 5%, var(--hs-surface, #ffffff));
}

.hs-table-head-sticky {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 96%, var(--hs-surface, #ffffff));
  color: var(--hs-muted, #64748b);
  z-index: 3;
}

.hs-table-cell-sticky {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  box-shadow: 10px 0 18px -18px rgba(15, 23, 42, 0.5);
  z-index: 2;
}

.hs-table-row:hover .hs-table-cell-sticky {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 7%, var(--hs-surface, #ffffff));
}

.hs-table-wrap :deep(td),
.hs-table-wrap :deep(th) {
  line-height: 1.35;
}

.hs-table-empty-cell {
  padding: 2rem;
}

.hs-table-empty {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 55%, transparent);
  border: 1px dashed color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 80%, transparent);
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  display: flex;
  gap: 0.85rem;
  justify-content: center;
  min-height: 7rem;
  text-align: left;
}

.hs-table-empty__icon {
  align-items: center;
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 11%, transparent);
  border-radius: 16px;
  color: var(--hs-primary, #3293ec);
  display: inline-flex;
  height: 3rem;
  justify-content: center;
  width: 3rem;
}

.hs-table-empty p {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  margin: 0 0 0.2rem;
}

.hs-table-empty span {
  font-size: 0.82rem;
  font-weight: 700;
}

@media (max-width: 640px) {
  table {
    min-width: max(680px, 100%);
  }

  th,
  td {
    white-space: nowrap;
  }
}

/* Add shadow to sticky columns */
.sticky {
  box-shadow: 10px 0 18px -18px rgba(15, 23, 42, 0.55);
}

.dark .sticky {
  box-shadow: 10px 0 18px -18px rgba(0, 0, 0, 0.68);
}
</style>
